import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ArrowRight, CheckCircle2, TriangleAlert } from 'lucide-react';
import { verifyEmail } from '../services/auth';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';

function VerifyEmailPage() {
  const { token } = useParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    const verifyToken = async () => {
      try {
        await verifyEmail(token);
        if (isActive) {
          setVerified(true);
        }
      } catch (requestError) {
        if (isActive) {
          setError(
            requestError.response?.data?.message
              || 'Verification failed. The link may be expired or invalid.'
          );
        }
      } finally {
        if (isActive) {
          setVerifying(false);
        }
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError('Verification link is missing a token.');
      setVerifying(false);
    }

    return () => {
      isActive = false;
    };
  }, [token]);

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Verify email"
        title="Check your verification status"
        description="We are confirming your email address so your account can be activated securely."
      />

      {verifying ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Verifying your email...
          </Typography>
        </Stack>
      ) : verified ? (
        <Stack spacing={2.5}>
          <Alert severity="success" icon={<CheckCircle2 size={18} />}>
            Your email has been verified successfully.
          </Alert>
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.72 }}>
            Your account is ready. Continue to login and start using Virtual Medic.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button component={Link} to="/login" variant="contained" sx={publicPrimaryButtonSx}>
              Go to login
            </Button>
            <Button component={Link} to="/downloads" variant="outlined" sx={publicSecondaryButtonSx}>
              View downloads
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          <Alert severity="error" icon={<TriangleAlert size={18} />}>
            {error}
          </Alert>
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.72 }}>
            If this link has expired, sign in to request another verification email or contact support.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Button component={Link} to="/login" variant="contained" sx={publicPrimaryButtonSx}>
              Go to login
            </Button>
            <Button component={Link} to="/signup" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
              Back to sign up
            </Button>
          </Box>
        </Stack>
      )}
    </PublicPageFrame>
  );
}

export default VerifyEmailPage;
